import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePublicLang } from "../../../config/i18n/public";

const SUPPORTERS = [
  { href: "https://www.hhi.fraunhofer.de/", src: "/fraunhofer-hhi.png", alt: "Fraunhofer HHI", size: "w-28 sm:w-36" },
  { href: "https://www.social-startup-hub.de/", src: "/SSHB.png", alt: "Social Startup Hub", size: "w-11 sm:w-12" },
  { href: "https://www.science-startups.berlin/", src: "/science-startups.png", alt: "Science & Startups", size: "w-28 sm:w-36" },
  { href: "https://roboinnovate.mirmi.tum.de/", src: "/robo-innovate.png", alt: "robo.innovate", size: "w-36 sm:w-48" },
  { href: "https://www.tum-venture-labs.de/", src: "/tum_venture_labs.png", alt: "TUM Venture Labs", size: "w-36 sm:w-44" },
  // `crop` compensates for the supplied logo's transparent top and bottom
  // padding. In percent (of the logo's width) so it holds when the logo scales.
  { href: "https://www.germanaccelerator.com/", src: "/german-accelerator.png", alt: "German Accelerator", size: "w-44 sm:w-52", crop: "-mt-[12.5%] -mb-[22%]" },
  { href: "https://science-startups.berlin/programs/berliner-startup-stipendium", src: "/berliner-startup-stipendium.png", alt: "Berliner Startup Stipendium", size: "w-36 sm:w-44" },
  { href: "https://www.tu.berlin/", src: "/tu-berlin.png", alt: "Technische Universität Berlin", size: "w-16 sm:w-20" },
  { href: "https://www.fu-berlin.de/", src: "/fu-berlin.png", alt: "Freie Universität Berlin", size: "w-20 sm:w-24" },
  { src: "/onstage-top-100.png", alt: "Onstage Top 100", size: "w-16 sm:w-20" },
];

export default function SteffiLanding({onMehrErfahren }) {
  const [chevronUp, setChevronUp] = useState(false);
  const { lang, setLang, copy } = usePublicLang();
  const c = copy.landing;

  useEffect(() => {
    const onScroll = () => {
      // Flip the chevron after the user leaves the very top of the page
      setChevronUp(window.scrollY > 80);
    };
    onScroll(); // initialize on mount
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden text-white">
      {/* Background media. Two crops of the same scene — the landscape frame
          keeps the headline clear of the device, the portrait one stacks them. */}
      <div aria-hidden className="absolute inset-0">
        <img
          src="/homepage/hero/care-hero-face-centered.webp"
          alt=""
          fetchPriority="high"
          className="hidden sm:block h-full w-full object-cover"
        />
        <img
          src="/homepage/hero/care-hero-mobile-face-centered.webp"
          alt=""
          fetchPriority="high"
          className="block sm:hidden h-full w-full object-cover"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Header */}
      <header className="absolute inset-x-0 top-0 z-20 hidden sm:block">
        <div className="mx-auto mt-3 w-full px-3">
          <div className="flex items-center justify-between rounded-2xl bg-white/20 px-4 py-2 sm:px-6 sm:py-3 backdrop-blur-md">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 hover:opacity-80">
              <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-white border border-[#DEF1FF] overflow-hidden shadow-sm">
                <img
                  src="/steffi/logo.png"
                  alt="Logo"
                  className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
                />
              </div>
              <span className="ml-2 text-xl sm:text-2xl font-bold tracking-wide text-white">
                SteffiCare
              </span>
            </Link>

            {/* Nav + Login */}
            <div className="flex items-center gap-2 sm:gap-6 flex-wrap justify-end">
              {/* On a phone only the other language is offered: the button for the one
                  you are already reading takes room in a full bar without saying
                  anything. Both pills return from `sm` up. */}
              <button
                type="button"
                onClick={() => setLang(lang === "de" ? "en" : "de")}
                className="w-[38px] flex-none rounded-lg border border-white/20 bg-white/10 py-1.5 text-center text-xs font-semibold text-white/90 hover:bg-white/20 sm:hidden"
              >
                {lang === "de" ? copy.lang.en : copy.lang.de}
              </button>

              <div className="hidden items-center rounded-lg border border-white/20 bg-white/10 p-1 flex-none sm:flex">
                <button
                  type="button"
                  onClick={() => setLang("de")}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-md ${
                    lang === "de" ? "bg-white text-slate-900" : "text-white/90 hover:bg-white/10"
                  }`}
                >
                  {copy.lang.de}
                </button>
                <button
                  type="button"
                  onClick={() => setLang("en")}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-md ${
                    lang === "en" ? "bg-white text-slate-900" : "text-white/90 hover:bg-white/10"
                  }`}
                >
                  {copy.lang.en}
                </button>
              </div>
              <Link
                to="/angehoerige"
                className="inline-flex rounded-xl border border-[#e8ddd3] bg-[#fcf7f3] px-4 py-2 text-sm font-semibold text-[#24211e] shadow-md transition hover:bg-[#f8f2ec]"
              >
                {c.for_family}
              </Link>
              <button 
                onClick={onMehrErfahren} 
                className="hidden sm:inline-flex hover:opacity-90 bg-transparent"
              >
                {c.nav_contact}
              </button>
              {/* Hidden on a phone: the bar carries logo, the pilot CTA and the
                  language toggle already, and the login is not what a first-time
                  visitor came for. Returns from `sm` up. */}
              <Link
                to="/login"
                className="hidden rounded-xl bg-white/90 px-3 py-1 text-sm font-medium text-slate-900 shadow hover:bg-white sm:inline-flex"
              >
                {c.nav_login}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero content */}
      <main className="relative z-10 mx-auto flex h-full w-full max-w-[1480px] flex-col items-end justify-start px-4 pt-40 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h1 className="text-4xl font-extrabold leading-tight md:text-6xl">
            {c.hero_title_line1}{" "}
            <span className="text-orange-300">{c.hero_title_em}</span>
            <br />
            {c.hero_title_line2}
          </h1>
        <p className="mt-5 text-base leading-relaxed text-white/90 md:text-lg">
            {c.hero_body}
          </p>
          <Link to="/termin">
            <button
              className="mt-6 rounded-full bg-white/20 px-5 py-2 
                        text-base font-semibold text-white
                        backdrop-blur-sm shadow-md transition 
                        hover:translate-y-[-1px] hover:bg-white/30 
                        hover:shadow-lg active:translate-y-0"
            >
              {c.hero_cta}
            </button>
          </Link>
        </div>
      </main>

      {/* Bottom banner: always one row — label, logos, chevron. */}
      <div className="absolute inset-x-0 bottom-0 z-10">
        <div
          className="
            flex items-center justify-between gap-4
            bg-white/75 px-4 sm:px-6 py-3
            text-slate-900
            pb-[max(env(safe-area-inset-bottom),0.5rem)]
          "
        >
          {/* Group: text + logos */}
          <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-8 xl:gap-12">
            {/* The logos carry the message on their own where space is tight. */}
            <p className="hidden shrink-0 whitespace-nowrap text-base font-semibold sm:block sm:text-xl sm:mt-[-6px]">
              {c.supported_by}
            </p>

            {/* Below xl the logos don't fit one row at a readable size, so they
                run as one sliding strip instead of wrapping into several rows. */}
            <div
              className="marquee min-w-0 flex-1 xl:hidden"
              style={{ "--marquee-duration": `${SUPPORTERS.length * 4}s` }}
            >
              <div className="marquee-track">
                {[0, 1].map((copy) => (
                  <div
                    key={copy}
                    className="flex shrink-0 items-center gap-x-8 pr-8"
                    /* The second copy exists only to make the loop seamless —
                       a reader would otherwise hear every logo twice. */
                    aria-hidden={copy === 1 || undefined}
                  >
                    {SUPPORTERS.map((supporter) => (
                      <a
                        key={supporter.alt}
                        href={supporter.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        tabIndex={copy === 1 ? -1 : undefined}
                        className="flex shrink-0 items-center justify-center transition-opacity hover:opacity-75"
                      >
                        <img
                          src={supporter.src}
                          alt={copy === 1 ? "" : supporter.alt}
                          className={`${supporter.size} ${supporter.crop || ""} h-auto object-contain`}
                        />
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* xl and up: a still row. Each logo keeps its width while there is
                room and shrinks in proportion when there is not — never wraps. */}
            <div className="hidden min-w-0 flex-1 items-center justify-between gap-x-4 xl:flex">
              {SUPPORTERS.map((supporter) => (
                <a
                  key={supporter.alt}
                  href={supporter.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${supporter.size} flex min-w-0 items-center justify-center transition-opacity hover:opacity-75`}
                >
                  <img
                    src={supporter.src}
                    alt={supporter.alt}
                    className={`${supporter.crop || ""} h-auto w-full object-contain`}
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Chevron: hidden on mobile; flips up when scrolled */}
          <a
            href="#mehr"
            aria-label={c.next_section_aria}
            className="ml-6 shrink-0 hidden sm:block"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className={[
                "h-8 w-8 text-black transform transition-transform duration-300",
                chevronUp ? "rotate-180" : "rotate-0",
              ].join(" ")}
            >
              <path
                fillRule="evenodd"
                d="M11.47 15.78a.75.75 0 001.06 0l6-6a.75.75 0 10-1.06-1.06L12 14.19 6.53 8.72a.75.75 0 10-1.06 1.06l6 6z"
                clipRule="evenodd"
              />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}
